import React, { FC, useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { getEntities as getShelfUnits } from '../../redux/actions/shelfUnitsActions';
import { getEntities as getUnitsOfMeasures } from '../../redux/actions/unitsOfMeasuresActions';
import { BRANDS } from '../../types/brands';
import { TCategoryProductSheetResponse } from '../../types/product';
import { useProductionSheetsStrings } from './constants/ProductionSheetsStrings';
import CommodityCategoryView from './CommodityCategoryView';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useStyles from './styles';
import { textToSnakeCase } from '../../utils/utils';
import { useTranslation } from 'react-i18next';

interface ProductionSheetsViewProps {
  productSheet: TCategoryProductSheetResponse;
  profile: any;
}

const ProductionSheetsView: FC<ProductionSheetsViewProps> = ({
  productSheet,
  profile,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch: any = useDispatch();
  const ProductionSheetsStrings = useProductionSheetsStrings();

  const { isTestMarketOn } = useFlags();
  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const [defaultUnitsOfMeasure, setDefaultUnitsOfMeasure] = useState('');

  const shelfUnits = useSelector(
    (state: RootStateOrAny) => state.ShelfUnitsReducer.entities,
  );

  const shelfUnitNamesById = shelfUnits.reduce((acc, each) => {
    acc[each.id] = each.name;
    return acc;
  }, {});

  const unitsOfMeasures = useSelector(
    (state: RootStateOrAny) => state.UnitsOfMeasuresReducer.entities,
  );

  const unitsOfMeasuresById = unitsOfMeasures.reduce((acc, each) => {
    acc[each.id] = each.name;
    return acc;
  }, {});

  useEffect(() => {
    if (selectedBrand === BRANDS.TH) {
      dispatch(getShelfUnits());
      dispatch(getUnitsOfMeasures());
    }
  }, [selectedBrand]);

  useEffect(() => {
    if (productSheet) {
      setDefaultUnitsOfMeasure(
        productSheet?.unitsOfMeasure?.default ||
          productSheet?.unitsOfMeasure?.units?.find(unit => unit?.is_default),
      );
    }
  }, [productSheet]);

  if (!productSheet) {
    return null;
  }

  return (
    <div>
      <div className={classes.flexStart}>
        <div className={classes.attributeItem}>
          {productSheet.display ? <CheckIcon /> : <CloseIcon />}
          <Typography variant="caption">
            {ProductionSheetsStrings.production_sheets}
          </Typography>
        </div>
        <div className={classes.attributeItem}>
          {productSheet.fresh ? <CheckIcon /> : <CloseIcon />}
          <Typography variant="caption">
            {ProductionSheetsStrings.fresh_check_required}
          </Typography>
        </div>
        {profile.region == 'NA' && isTestMarketOn && (
          <div className={classes.attributeItem}>
            {profile.is_test_market ? <CheckIcon /> : <CloseIcon />}
            <Typography variant="caption">
              {ProductionSheetsStrings.is_test_market}
            </Typography>
          </div>
        )}
      </div>
      {productSheet.display && (
        <div className={classes.flexSpaceAround}>
          <div>
            <Typography variant="h6">
              {ProductionSheetsStrings.shelf_life}
            </Typography>
            <Typography variant="body2">
              {[
                productSheet.shelfLife,
                shelfUnitNamesById[productSheet.shelfLifeUnit],
              ].join(' ')}
            </Typography>
          </div>
          <div>
            <div>
              <Typography variant="h6">
                {ProductionSheetsStrings.units_of_measure}
              </Typography>
            </div>
            {productSheet.unitsOfMeasure.units.map(unit => (
              <Typography key={unit} variant="body2">
                {unitsOfMeasuresById[unit]
                  ? t(
                      `app.common.shelfUnits.${textToSnakeCase(
                        unitsOfMeasuresById[unit],
                      )}`,
                      unitsOfMeasuresById[unit],
                    )
                  : null}
              </Typography>
            ))}
          </div>
          <div>
            <Typography variant="h6">
              {ProductionSheetsStrings.default_units_of_measure}
            </Typography>
            <Typography variant="body2">
              {unitsOfMeasuresById[defaultUnitsOfMeasure]
                ? t(
                    `app.common.shelfUnits.${textToSnakeCase(
                      unitsOfMeasuresById[defaultUnitsOfMeasure],
                    )}`,
                    unitsOfMeasuresById[defaultUnitsOfMeasure],
                  )
                : null}
            </Typography>
          </div>
        </div>
      )}

      {productSheet.display && (
        <CommodityCategoryView productSheet={productSheet} />
      )}
    </div>
  );
};

export default ProductionSheetsView;
